export const MONTH_MAPPING_KEY = "month_mapping_by_fiscal_year";
export const FORECAST_MONTH_MAPPING_KEY = "forecast_months_by_fiscal_year";

/**
 * Class responsible for transforming fiscal data into row format
 */
class RowTransformer {
  /**
   * Constructor initializes the rows collection
   */
  constructor() {
    this.rows = [];
  }

  /**
   * Transforms fiscal data into row format
   * @param {Object} data - The fiscal data object
   * @returns {Array} An array of formatted rows
   */
  transform(data) {
    this.rows = [];

    // Process each fiscal year's data from the new structure
    if (data.data && Array.isArray(data.data)) {
      data.data.forEach((yearData) => {
        const { fiscal_year, fiscal_year_type, fiscal_year_version } = yearData;
        // Create a unique key for the fiscal year including type and version
        const fiscalYearKey = fiscal_year_version
          ? `${fiscal_year}|${fiscal_year_type}|${fiscal_year_version}`
          : `${fiscal_year}|${fiscal_year_type}`;

        // Process financial data for this fiscal year
        if (yearData.financial_data && Array.isArray(yearData.financial_data)) {
          yearData.financial_data.forEach((input) => {
            this.processInputData(input, fiscalYearKey);
          });
        }
      });
    }

    return this.rows;
  }

  /**
   * Process a single input data and update or create rows accordingly
   * @param {Object} input - The current input data
   * @param {string} fiscalYearKey - The fiscal year key including type and version
   */
  processInputData(input, fiscalYearKey) {
    // Determine if this is forecast data based on fiscal year key
    const isForecast = fiscalYearKey.includes("|F");

    // First process the group itself
    const existingRow = this.findRowByName(input.account_parameter_name);

    if (existingRow) {
      this.updateExistingRow(existingRow, input, fiscalYearKey, isForecast);
    } else {
      this.createNewRow(input, fiscalYearKey, isForecast);
    }

    // Then process children if present
    if (input.children && input.children.length > 0) {
      input.children.forEach((child) => {
        const childRow = this.findRowByName(child.account_parameter_name);

        if (childRow) {
          this.updateExistingRow(childRow, child, fiscalYearKey, isForecast);
          // Set associated_to property for ordering
          childRow.associated_to = input.account_parameter_name;
        } else {
          this.createNewRow(
            child,
            fiscalYearKey,
            isForecast,
            input.account_parameter_name,
          );
        }

        // Link child to group
        if (input.is_group) {
          const childRow = this.findRowByName(child.account_parameter_name);
          if (childRow) {
            childRow.parent_group = input.account_parameter_name;
          }
        }
      });
    }
  }

  /**
   * Find a row by name
   * @param {string} name - The name to search for
   * @returns {Object|undefined} The found row or undefined
   */
  findRowByName(name) {
    return this.rows.find((row) => row.name === name);
  }

  /**
   * Updates an existing row with new fiscal data
   * @param {Object} row - The existing row to update
   * @param {Object} input - The input data
   * @param {string} fiscalYearKey - The fiscal year key
   * @param {boolean} isForecast - Whether the data is forecast data
   */
  updateExistingRow(row, input, fiscalYearKey, isForecast) {
    // Ignorer complètement les separator rows
    if (row.is_separator) {
      return;
    }
    // Initialize fiscal year object if it doesn't exist
    if (!row[MONTH_MAPPING_KEY][fiscalYearKey]) {
      row[MONTH_MAPPING_KEY][fiscalYearKey] = {};
    }

    // Initialize forecast months object if it doesn't exist
    if (!row[FORECAST_MONTH_MAPPING_KEY][fiscalYearKey]) {
      row[FORECAST_MONTH_MAPPING_KEY][fiscalYearKey] = {};
    }

    this.addMonthData(
      row[MONTH_MAPPING_KEY][fiscalYearKey],
      row[FORECAST_MONTH_MAPPING_KEY][fiscalYearKey],
      input.inputs,
      isForecast,
    );

    // Update group status and other properties if they exist in the input
    if (input.is_group !== undefined) row.is_group = input.is_group;
    if (input.order !== undefined) row.order = input.order;
    if (input.display_children !== undefined)
      row.display_children = input.display_children;
    if (input.color_text !== undefined) row.color_text = input.color_text;
    if (input.color_background !== undefined)
      row.color_background = input.color_background;
    if (input.is_ton !== undefined) row.is_ton = input.is_ton;
    if (input.is_percent !== undefined) row.is_percent = input.is_percent;
  }

  /**
   * Creates a new row and adds it to the rows collection
   * @param {Object} input - The input data
   * @param {string} fiscalYearKey - The fiscal year key
   * @param {boolean} isForecast - Whether the data is forecast data
   * @param {string|null} associatedTo - The name of the parent this row is associated with
   */
  createNewRow(input, fiscalYearKey, isForecast, associatedTo = null) {
    // If line_above is true, add an empty separator row
    if (input.line_above) {
      this.addSeparatorRow(input);
    }
    // Initialize the row with basic information
    const newRow = {
      name: input.account_parameter_name,
      order: input.order,
      is_group: input.is_group,
      line_above: input.line_above,
      display_children: input.display_children,
      color_text: input.color_text,
      color_background: input.color_background,
      is_percent: input.is_percent,
      is_ton: input.is_ton,
      is_children: input.is_children,
      is_decimal: input.is_decimal,
      is_separator: false,
      parent_group: null, // Will be set if this row belongs to a group
      associated_to: associatedTo, // Set the associated_to property
      account_parameter_id: input.account_parameter_id,
      comments: input?.comment,
      [MONTH_MAPPING_KEY]: {
        [fiscalYearKey]: {},
      },
      [FORECAST_MONTH_MAPPING_KEY]: {
        [fiscalYearKey]: {},
      },
    };

    // Add month data
    this.addMonthData(
      newRow[MONTH_MAPPING_KEY][fiscalYearKey],
      newRow[FORECAST_MONTH_MAPPING_KEY][fiscalYearKey],
      input.inputs,
      isForecast,
    );

    this.rows.push(newRow);
  }

  /**
   * Adds month data to target objects
   * @param {Object} target - The target object to add month data to
   * @param {Object} forecastTarget - The target object to add forecast month data to
   * @param {Array} inputs - Array of month data
   * @param {boolean} isForecast - Whether all data is from a forecast fiscal year
   */
  addMonthData(target, forecastTarget, inputs, isForecast) {
    if (!inputs) return;

    inputs.forEach((monthData) => {
      const { month, amount } = monthData;

      // Always add the amount to the regular month mapping
      target[month] = amount;

      // If this is forecast data, mark it in the forecast mapping
      if (isForecast) {
        forecastTarget[month] = true;
      }
    });
  }

  /**
   * Adds an empty separator row
   * @param {Object} input - The input data
   */
  addSeparatorRow(input) {
    const separatorRow = {
      name: "",
      order: input.order,
      is_separator_for: input.account_parameter_name,
      account_parameter_id: input.account_parameter_id,
      is_separator: true, // Flag clair
      [MONTH_MAPPING_KEY]: null, // Pas de données
      [FORECAST_MONTH_MAPPING_KEY]: null,
    };

    this.rows.push(separatorRow);
  }

  /**
   * Reorganizes rows to ensure groups appear before their associates
   * This happens after all rows are processed
   */
  reorganizeRowsByGroups() {
    const groupedRows = [];
    const groupRowMap = new Map();
    const separatorRowMap = new Map();

    // First pass: identify all group rows and separator rows
    this.rows.forEach((row) => {
      if (row.is_group) {
        groupRowMap.set(row.name, []);
      }
      if (row.is_separator_for) {
        separatorRowMap.set(row.is_separator_for, row);
      }
    });

    // Second pass: associate rows with their groups
    this.rows.forEach((row) => {
      if (row.parent_group && groupRowMap.has(row.parent_group)) {
        groupRowMap.get(row.parent_group).push(row);
      } else if (!row.parent_group && !row.is_separator_for) {
        groupedRows.push(row);
      }
    });

    // Sort the top-level rows by their order property
    groupedRows.sort((a, b) => (a.order || 0) - (b.order || 0));

    // Third pass: reorganize rows
    this.rows = [];

    groupedRows.forEach((row) => {
      // Add separator first if this row has one
      if (separatorRowMap.has(row.name)) {
        this.rows.push(separatorRowMap.get(row.name));
      }

      // Add the row itself
      this.rows.push(row);

      if (row.is_group && groupRowMap.has(row.name)) {
        // Sort associates by order
        const associates = groupRowMap.get(row.name);
        associates.sort((a, b) => (a.order || 0) - (b.order || 0));

        // Add associates after the group
        associates.forEach((associate) => {
          // Add separator first if this associate has one
          if (separatorRowMap.has(associate.name)) {
            this.rows.push(separatorRowMap.get(associate.name));
          }
          // Add the associate
          this.rows.push(associate);
        });
      }
    });
  }
}

/**
 * Transforms fiscal data into row format
 * @param {Object} data - The fiscal data object
 * @returns {Array} An array of formatted rows
 */
export const formDataToRow = (data) => {
  const transformer = new RowTransformer();
  transformer.transform(data);
  transformer.reorganizeRowsByGroups();

  return transformer.rows;
};
